import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import * as Icon from "react-bootstrap-icons";
import CustomButton from "../../../../../pages/reusable_components/custom_button_component/Button";
import { FieldProps } from "../../../InputFieldsController/InputFieldsController.utils";
import alertModalStyles from "../../../AlertModal/AlertModal.module.css";
import styles from "./SystemFieldDrawer.module.css";
import {
  createCustomSystemField,
  getAllSystemFields,
  getSelectedSystemFields,
  saveSystemFieldSelection,
} from "./SystemFieldDrawer.service";
import {
  buildSelectedCodesForClientType,
  FIELD_TYPE_OPTIONS,
  filterSystemFields,
  findCreatedField,
  getFieldDisplayName,
  isUnmappedSystemField,
  patchFieldWithSystemField,
  resolveClientTypesFromFields,
} from "./SystemFieldDrawer.utils";
import {
  CreateFieldFormState,
  ReferenceDataOption,
  SystemField,
  SystemFieldDrawerProps,
} from "./SystemFieldDrawer.types";

type ResolveMode = "map" | "create";

const emptyOptions = [{ id: 1, label: "", value: "" }];

const SystemFieldDrawer: React.FC<SystemFieldDrawerProps> = ({
  show,
  fields,
  initialFieldId,
  systemFields,
  categoryOptions,
  fieldDataLoading = false,
  onSystemFieldsRefresh,
  onSystemFieldListChanged,
  onClose,
  onFieldsResolved,
  onSystemFieldCreated,
}) => {
  const unmappedFields = useMemo(
    () => fields.filter(isUnmappedSystemField),
    [fields]
  );

  const [activeFieldId, setActiveFieldId] = useState<string>("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveMode, setResolveMode] = useState<ResolveMode>("map");
  const [resolveSearchText, setResolveSearchText] = useState("");
  const [selectedSystemField, setSelectedSystemField] = useState<SystemField | null>(null);
  const [resolveError, setResolveError] = useState("");
  const [createError, setCreateError] = useState("");
  const [createProgress, setCreateProgress] = useState(false);
  const [createForm, setCreateForm] = useState<CreateFieldFormState>({
    fieldName: "",
    fieldType: FIELD_TYPE_OPTIONS[0],
    category: null,
    options: emptyOptions,
  });

  const activeField = fields.find((field) => field.id === activeFieldId);
  const selectMenuPortalTarget =
    typeof document !== "undefined" ? document.body : undefined;
  const selectStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 3000 }),
    menu: (base: any) => ({ ...base, zIndex: 3000 }),
    control: (base: any, state: any) => ({
      ...base,
      minHeight: 36,
      borderColor: state.isFocused ? "var(--theme1_primary, #2563eb)" : "#d1d5db",
      borderRadius: 4,
      backgroundColor: "#ffffff",
      boxShadow: state.isFocused
        ? "0 0 0 2px color-mix(in srgb, var(--theme1_tertiary, #f97316) 18%, transparent)"
        : "none",
      "&:hover": {
        borderColor: state.isFocused ? "var(--theme1_primary, #2563eb)" : "#9ca3af",
      },
    }),
    option: (base: any, state: any) => ({
      ...base,
      fontSize: 12,
      color: "#111827",
      backgroundColor: state.isSelected
        ? "var(--theme1_tertiary, #f97316)"
        : state.isFocused
          ? "#f3f4f6"
          : "#ffffff",
    }),
    singleValue: (base: any) => ({ ...base, color: "#111827", fontSize: 12 }),
    placeholder: (base: any) => ({ ...base, color: "#6b7280", fontSize: 12 }),
  };

  const getDefaultCategory = (targetField?: FieldProps) => {
    const candidate = String(targetField?.allfields_category ?? "").trim();
    return (
      categoryOptions.find((option) => option.value === candidate) ??
      categoryOptions.find((option) => option.value.toLowerCase().includes("custom")) ??
      categoryOptions[0] ??
      null
    );
  };

  const buildCreateForm = (targetField?: FieldProps): CreateFieldFormState => ({
    fieldName: targetField ? getFieldDisplayName(targetField).replace(/:$/, "") : "",
    fieldType:
      FIELD_TYPE_OPTIONS.find((option) => option.value === targetField?.type) ??
      FIELD_TYPE_OPTIONS[0],
    category: getDefaultCategory(targetField),
    options:
      targetField?.type === "select" &&
      Array.isArray(targetField.options) &&
      targetField.options.length
        ? targetField.options.map((option: any, index) => ({
            id: index + 1,
            label: String(option.label ?? option.value ?? ""),
            value: String(option.value ?? option.label ?? ""),
          }))
        : emptyOptions,
  });

  const visibleSystemFields = useMemo(
    () => filterSystemFields(systemFields, resolveSearchText),
    [systemFields, resolveSearchText]
  );

  useEffect(() => {
    if (!show) return;

    setActiveFieldId(initialFieldId || unmappedFields[0]?.id || "");
  }, [show, initialFieldId, unmappedFields]);

  useEffect(() => {
    if (!show || typeof document === "undefined") return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [show]);

  useEffect(() => {
    if (!showCreateModal) return;

    setCreateForm(buildCreateForm());
    setCreateError("");
  }, [showCreateModal, categoryOptions]);

  const markFieldSelected = async (fieldCode: string) => {
    const clientTypes = resolveClientTypesFromFields(fields);
    const selectedRecords = await getSelectedSystemFields();

    for (let index = 0; index < clientTypes.length; index++) {
      const clientType = clientTypes[index];
      const codes = buildSelectedCodesForClientType(selectedRecords, clientType, fieldCode);
      const ok = await saveSystemFieldSelection(
        clientType,
        codes,
        index === clientTypes.length - 1
      );
      if (!ok) return false;
    }

    return true;
  };

  const openResolveModal = (field: FieldProps) => {
    setActiveFieldId(field.id);
    setResolveMode("map");
    setSelectedSystemField(null);
    setResolveSearchText(getFieldDisplayName(field).replace(/:$/, ""));
    setResolveError("");
    setCreateForm(buildCreateForm(field));
    setShowResolveModal(true);
  };

  const closeResolveModal = () => {
    setShowResolveModal(false);
    setResolveError("");
    setSelectedSystemField(null);
  };

  const validateCreateForm = (setError: (message: string) => void) => {
    const fieldName = createForm.fieldName.trim();
    if (!fieldName) {
      setError("Field name is required.");
      return null;
    }

    if (!createForm.category?.value) {
      setError("Category is required.");
      return null;
    }

    const cleanOptions = createForm.options.filter((option) => option.value.trim());
    if (createForm.fieldType.value === "select" && cleanOptions.length === 0) {
      setError("Add at least one dropdown option.");
      return null;
    }

    return { fieldName, cleanOptions };
  };

  const createSystemFieldFromForm = async (
    setError: (message: string) => void
  ): Promise<SystemField | null> => {
    const validForm = validateCreateForm(setError);
    if (!validForm) return null;

    setCreateProgress(true);
    setError("");
    try {
      const created = await createCustomSystemField({
        field_name: validForm.fieldName,
        field_type: createForm.fieldType.value,
        field_category: createForm.category?.value || "",
        field_purpose: ["Client Data Management", "Engagement Letter"],
        field_options:
          createForm.fieldType.value === "select" ? validForm.cleanOptions : [],
      });

      if (!created) {
        setError("Field could not be created. Please try again.");
        return null;
      }

      const refreshedFields = onSystemFieldsRefresh
        ? await onSystemFieldsRefresh()
        : await getAllSystemFields();

      const createdField = findCreatedField(refreshedFields, validForm.fieldName);
      if (!createdField) {
        setError(
          "Field was created, but it was not returned by the field list yet. Please search for it and select it."
        );
        return null;
      }

      return createdField;
    } finally {
      setCreateProgress(false);
    }
  };

  const updateFieldFromSystemField = (
    targetField: FieldProps,
    systemField: SystemField
  ) => {
    const patched = fields.map((field) =>
      field.id === targetField.id ? patchFieldWithSystemField(field, systemField) : field
    );
    onFieldsResolved(patched);

    const nextUnmapped = patched.find(isUnmappedSystemField);
    setActiveFieldId(nextUnmapped?.id || "");
  };

  const handleStandaloneCreate = async () => {
    const createdField = await createSystemFieldFromForm(setCreateError);
    if (!createdField) return;

    const selected = await markFieldSelected(createdField.field_code);
    if (!selected) {
      setCreateError("Field was created, but could not be selected for this client type.");
      return;
    }

    onSystemFieldCreated?.(createdField);
    await onSystemFieldListChanged?.();
    setShowCreateModal(false);
    onClose();
  };

  const handleResolveOkay = async () => {
    if (!activeField) {
      setResolveError("Choose an unresolved field first.");
      return;
    }

    if (resolveMode === "map") {
      if (!selectedSystemField) {
        setResolveError("Choose an existing field to map.");
        return;
      }

      updateFieldFromSystemField(activeField, selectedSystemField);
      closeResolveModal();
      return;
    }

    const createdField = await createSystemFieldFromForm(setResolveError);
    if (!createdField) return;

    const selected = await markFieldSelected(createdField.field_code);
    if (!selected) {
      setResolveError("Field was created, but could not be selected for this client type.");
      return;
    }

    updateFieldFromSystemField(activeField, createdField);
    await onSystemFieldListChanged?.();
    closeResolveModal();
  };

  const updateOption = (id: number, value: string) => {
    setCreateForm((previous) => ({
      ...previous,
      options: previous.options.map((option) =>
        option.id === id ? { ...option, label: value, value } : option
      ),
    }));
  };

  const addOption = () => {
    setCreateForm((previous) => ({
      ...previous,
      options: [
        ...previous.options,
        { id: Date.now(), label: "", value: "" },
      ],
    }));
  };

  const removeOption = (id: number) => {
    setCreateForm((previous) => ({
      ...previous,
      options: previous.options.filter((option) => option.id !== id),
    }));
  };

  if (!show) return null;

  return (
    <>
      <div className={styles.drawerBackdrop} onMouseDown={onClose}>
        <aside className={styles.drawer} onMouseDown={(event) => event.stopPropagation()}>
          <div className={styles.drawerHeader}>
            <div>
              <h3 className={styles.title}>Manage fields</h3>
            </div>
            <button
              type="button"
              className={`btn-close ${styles.closeButton}`}
              onClick={onClose}
              aria-label="Close"
            />
          </div>

          <div className={styles.drawerBody}>
            <div className={styles.drawerActionBar}>
              <CustomButton
                content={
                  <span className={styles.buttonContent}>
                    <Icon.PlusLg />
                    <span>Add Custom Field</span>
                  </span>
                }
                type="tertiary"
                onclick={() => {
                  setCreateForm(buildCreateForm());
                  setCreateError("");
                  setShowCreateModal(true);
                }}
              />
            </div>

            <div className={styles.drawerDivider}>or</div>

            <section className={styles.panel}>
              <div className={styles.sectionHeader}>
                <h4 className={styles.panelTitle}>Fields to connect</h4>
                {unmappedFields.length > 0 && (
                  <span className={styles.countPill}>{unmappedFields.length}</span>
                )}
              </div>
              <p className={styles.sectionDescription}>
                Select a field to map it to an existing system field, or create a new system field for it.
              </p>

              {unmappedFields.length ? (
                <div className={styles.fieldList}>
                  {unmappedFields.map((field) => (
                    <button
                      key={field.id}
                      type="button"
                      className={`${styles.unmappedFieldButton} ${
                        field.id === activeFieldId ? styles.unmappedFieldButtonActive : ""
                      }`}
                      onClick={() => openResolveModal(field)}
                    >
                      <span>
                        <p className={styles.fieldName}>{getFieldDisplayName(field)}</p>
                        <p className={styles.fieldMeta}>
                          {field.type} - {field.allfields_code || "temporary"}
                        </p>
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className={styles.emptyState}>
                  All visible form fields are connected to system fields.
                </p>
              )}
            </section>
          </div>
        </aside>
      </div>

      <Modal
        show={showResolveModal}
        onHide={closeResolveModal}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName={alertModalStyles.dialog}
        contentClassName={alertModalStyles.content}
        backdropClassName={alertModalStyles.backdrop}
      >
        <Modal.Header closeButton className={alertModalStyles.header}>
          <Modal.Title className={alertModalStyles.message}>Resolve Field</Modal.Title>
        </Modal.Header>
        <Modal.Body className={`${alertModalStyles.body} ${styles.modalBody}`}>
          {activeField && (
            <div className={styles.resolveFieldSummary}>
              <div>
                <p className={styles.fieldName}>{getFieldDisplayName(activeField)}</p>
                <p className={styles.fieldMeta}>
                  {activeField.type} - {activeField.allfields_code || "temporary"}
                </p>
              </div>
              <span className={styles.pill}>Not in system</span>
            </div>
          )}

          <div className={styles.modeSwitch}>
            <button
              type="button"
              className={`${styles.modeButton} ${
                resolveMode === "map" ? styles.modeButtonActive : ""
              }`}
              onClick={() => {
                setResolveMode("map");
                setResolveError("");
              }}
            >
              Map Existing
            </button>
            <button
              type="button"
              className={`${styles.modeButton} ${
                resolveMode === "create" ? styles.modeButtonActive : ""
              }`}
              onClick={() => {
                setResolveMode("create");
                setResolveError("");
                setCreateForm(buildCreateForm(activeField));
              }}
            >
              Create New
            </button>
          </div>

          {resolveMode === "map" ? (
            <div className={styles.formRow}>
              <label htmlFor="cygnus-resolve-search">Find an existing field</label>
              <input
                id="cygnus-resolve-search"
                className={styles.searchInput}
                value={resolveSearchText}
                onChange={(event) => {
                  setResolveSearchText(event.target.value);
                  setSelectedSystemField(null);
                  setResolveError("");
                }}
                placeholder="Search by name, code, category, or type"
              />
              <div className={styles.resultsList}>
                {fieldDataLoading ? (
                  <p className={styles.helperText}>Loading fields...</p>
                ) : visibleSystemFields.length ? (
                  visibleSystemFields.slice(0, 40).map((field) => (
                    <button
                      key={field.id}
                      type="button"
                      className={`${styles.resultButton} ${
                        selectedSystemField?.id === field.id
                          ? styles.resultButtonActive
                          : ""
                      }`}
                      onClick={() => {
                        setSelectedSystemField(field);
                        setResolveError("");
                      }}
                    >
                      <p className={styles.fieldName}>{field.field_name}</p>
                      <div className={styles.resultCategory}>
                        {field.category} - {field.type} - {field.field_code}
                      </div>
                    </button>
                  ))
                ) : (
                  <p className={styles.helperText}>No matching fields found.</p>
                )}
              </div>
            </div>
          ) : (
            <>
              <p className={styles.helperText}>
                Create a system field for this unresolved form field. It will be mapped after you confirm.
              </p>

              <CreateFieldForm
                createForm={createForm}
                categoryOptions={categoryOptions}
                selectMenuPortalTarget={selectMenuPortalTarget}
                selectStyles={selectStyles}
                setCreateForm={setCreateForm}
                addOption={addOption}
                updateOption={updateOption}
                removeOption={removeOption}
              />
            </>
          )}

          {resolveError && <p className={styles.errorText}>{resolveError}</p>}
        </Modal.Body>
        <Modal.Footer className={alertModalStyles.footer}>
          <CustomButton content="Cancel" type="quinary" onclick={closeResolveModal} />
          <CustomButton
            content="Okay"
            type="tertiary"
            onclick={handleResolveOkay}
            progress={createProgress}
          />
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName={alertModalStyles.dialog}
        contentClassName={alertModalStyles.content}
        backdropClassName={alertModalStyles.backdrop}
      >
        <Modal.Header closeButton className={alertModalStyles.header}>
          <Modal.Title className={alertModalStyles.message}>Add Custom Field</Modal.Title>
        </Modal.Header>
        <Modal.Body className={`${alertModalStyles.body} ${styles.modalBody}`}>
          <CreateFieldForm
            createForm={createForm}
            categoryOptions={categoryOptions}
            selectMenuPortalTarget={selectMenuPortalTarget}
            selectStyles={selectStyles}
            setCreateForm={setCreateForm}
            addOption={addOption}
            updateOption={updateOption}
            removeOption={removeOption}
          />

          {createError && <p className={styles.errorText}>{createError}</p>}
        </Modal.Body>
        <Modal.Footer className={alertModalStyles.footer}>
          <CustomButton content="Cancel" type="quinary" onclick={() => setShowCreateModal(false)} />
          <CustomButton
            content="Create"
            type="tertiary"
            onclick={handleStandaloneCreate}
            progress={createProgress}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};

interface CreateFieldFormProps {
  createForm: CreateFieldFormState;
  categoryOptions: ReferenceDataOption[];
  selectMenuPortalTarget?: HTMLElement;
  selectStyles: Record<string, any>;
  setCreateForm: React.Dispatch<React.SetStateAction<CreateFieldFormState>>;
  addOption: () => void;
  updateOption: (id: number, value: string) => void;
  removeOption: (id: number) => void;
}

const CreateFieldForm: React.FC<CreateFieldFormProps> = ({
  createForm,
  categoryOptions,
  selectMenuPortalTarget,
  selectStyles,
  setCreateForm,
  addOption,
  updateOption,
  removeOption,
}) => (
  <>
    <div className={styles.formRow}>
      <label htmlFor="cygnus-system-field-name">Field Name</label>
      <input
        id="cygnus-system-field-name"
        className={styles.textInput}
        value={createForm.fieldName}
        onChange={(event) =>
          setCreateForm((previous) => ({ ...previous, fieldName: event.target.value }))
        }
        placeholder="e.g. Primary Partner"
      />
    </div>

    <div className={styles.formRow}>
      <label>Field Type</label>
      <Select
        value={createForm.fieldType}
        options={FIELD_TYPE_OPTIONS}
        onChange={(option) =>
          option && setCreateForm((previous) => ({ ...previous, fieldType: option }))
        }
        classNamePrefix="react-select"
        menuPosition="fixed"
        menuPortalTarget={selectMenuPortalTarget}
        styles={selectStyles}
        isSearchable={false}
        placeholder="Select field type"
      />
    </div>

    <div className={styles.formRow}>
      <label>Category</label>
      <Select
        value={createForm.category}
        options={categoryOptions}
        onChange={(option) =>
          setCreateForm((previous) => ({ ...previous, category: option }))
        }
        classNamePrefix="react-select"
        menuPosition="fixed"
        menuPortalTarget={selectMenuPortalTarget}
        styles={selectStyles}
        placeholder="Select category"
      />
    </div>

    {createForm.fieldType.value === "select" && (
      <div className={styles.formRow}>
        <div className={styles.optionHeader}>
          <label>Dropdown Options</label>
          <button type="button" className={styles.iconButton} onClick={addOption} title="Add option">
            <Icon.PlusLg />
          </button>
        </div>
        <div className={styles.optionList}>
          {createForm.options.map((option) => (
            <div key={option.id} className={styles.optionRow}>
              <input
                className={styles.textInput}
                value={option.value}
                onChange={(event) => updateOption(option.id, event.target.value)}
                placeholder="Option value"
              />
              <button
                type="button"
                className={styles.iconButton}
                onClick={() => removeOption(option.id)}
                title="Remove option"
              >
                <Icon.DashLg />
              </button>
            </div>
          ))}
        </div>
      </div>
    )}
  </>
);

export default SystemFieldDrawer;
